import type { MachineRecord, ProductionCalendarDayRecord, ScheduleSlotRecord } from '../types/records'
import { resolvePlantIsWorking } from './productionCalendar'
import { availabilityPercent } from './machines'

export type ScheduleConflictType = 'overlap' | 'maintenance_warning' | 'plant_closed'

export type ScheduleConflict = {
  type: ScheduleConflictType
  message: string
  slotId?: string
}

/** Полуинтервал [start, end): пересечение двух слотов. */
export function intervalsOverlap(
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string,
): boolean {
  const as = new Date(aStart).getTime()
  const ae = new Date(aEnd).getTime()
  const bs = new Date(bStart).getTime()
  const be = new Date(bEnd).getTime()
  if (!Number.isFinite(as) || !Number.isFinite(ae) || !Number.isFinite(bs) || !Number.isFinite(be)) {
    return false
  }
  return as < be && bs < ae
}

export function findSlotConflicts(
  candidate: Pick<ScheduleSlotRecord, 'id' | 'machine' | 'plannedStart' | 'plannedEnd' | 'status'>,
  existing: ScheduleSlotRecord[],
): ScheduleConflict[] {
  const conflicts: ScheduleConflict[] = []

  for (const slot of existing) {
    if (slot.id === candidate.id) continue
    if (slot.status === 'cancelled') continue
    if (slot.machine !== candidate.machine) continue
    if (
      intervalsOverlap(
        candidate.plannedStart,
        candidate.plannedEnd,
        slot.plannedStart,
        slot.plannedEnd,
      )
    ) {
      conflicts.push({
        type: 'overlap',
        message: 'Пересечение с другим слотом на этом станке',
        slotId: slot.id,
      })
    }
  }

  return conflicts
}

export function maintenanceWarning(
  machine: Pick<MachineRecord, 'working_hours_per_day' | 'maintenance_hours_per_day'>,
): ScheduleConflict | null {
  if ((Number(machine.maintenance_hours_per_day) || 0) <= 0) return null
  const avail = availabilityPercent(machine)
  if (avail >= 100) return null
  return {
    type: 'maintenance_warning',
    message: `На станке зарезервировано ${machine.maintenance_hours_per_day} ч техобслуживания в сутки`,
  }
}

export function plantClosedWarning(
  plannedStartIso: string,
  calendarMap: Map<string, Pick<ProductionCalendarDayRecord, 'isWorking'>>,
): ScheduleConflict | null {
  const dateKey = plannedStartIso.slice(0, 10)
  if (resolvePlantIsWorking(dateKey, calendarMap)) return null
  return {
    type: 'plant_closed',
    message: 'День отмечен в календаре как нерабочий (простой или праздник)',
  }
}

export function collectScheduleWarnings(
  candidate: Pick<ScheduleSlotRecord, 'id' | 'machine' | 'plannedStart' | 'plannedEnd' | 'status'>,
  existing: ScheduleSlotRecord[],
  machine?: Pick<MachineRecord, 'working_hours_per_day' | 'maintenance_hours_per_day'> | null,
  calendarMap?: Map<string, Pick<ProductionCalendarDayRecord, 'isWorking'>>,
): ScheduleConflict[] {
  const out = findSlotConflicts(candidate, existing)
  const warn = machine ? maintenanceWarning(machine) : null
  if (warn) out.push(warn)
  if (calendarMap) {
    const closed = plantClosedWarning(candidate.plannedStart, calendarMap)
    if (closed) out.push(closed)
  }
  return out
}
